import { MenuButton, MenuItemWithIcon } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { isNil } from "lodash-es";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { ExportTemplatesMenuItem } from "../buttons/ExportTemplatesMenuItem.js";
import { QuoteTemplateManageVersionsMenuItem } from "./buttons/QuoteTemplateManageVersionsMenuItem.js";
import { buildDocPath } from "../../../utils.js";
import { BugReport as BugReportIcon } from "@mui/icons-material";
import { TemplateTypeIcon } from "../../templateTypes/TemplateTypeIcon.js";
import { TemplateQuoteTemplateVersionMenu_QuoteTemplateFragment } from "./TemplatesQuoteTemplateVersionMenu.generated.js";

interface Props {
  doc: TemplateQuoteTemplateVersionMenu_QuoteTemplateFragment;
  pathToDoc: string;
  pathToDocList: string;
  pathToDocVersion: string;
  onDuplicateRefetchQueries?: string[];
  onUpdateDataRefetchQueries?: string[];
}

export const TemplatesQuoteTemplateVersionMenu = ({
  doc,
  pathToDoc,
  pathToDocVersion,
  onDuplicateRefetchQueries,
  onUpdateDataRefetchQueries,
}: Props) => {
  const { t } = useTranslate(["Global", "Templates", "Quote"]);
  const navigate = useNavigate();

  const showUsedTemplateTypesItem = !isNil(
    doc.resolvedAsReadModelVersionNumber
  );

  return (
    <MenuButton>
      <RestrictedByCapabilityWithDebug capability="TEMPLATE_IMPORT_EXPORT">
        <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_ORG">
          <ExportTemplatesMenuItem
            docIds={[
              {
                docId: doc.id,
                versionNumber: doc.resolvedAsReadModelVersionNumber ?? null,
              },
            ]}
            title={t("Export template", { ns: "Quote" })}
          />
        </RestrictedByOrganisationPermissionWithDebug>
      </RestrictedByCapabilityWithDebug>
      <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
        <QuoteTemplateManageVersionsMenuItem
          docId={doc.id}
          pathToDoc={pathToDoc}
          refetchQueries={onUpdateDataRefetchQueries}
        />
      </RestrictedByOrganisationPermissionWithDebug>
      <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
        <MenuItemWithIcon
          icon={<BugReportIcon />}
          onClick={() => navigate(`${pathToDocVersion}/troubleshoot`)}
        >
          {t("Troubleshoot", { ns: "Global" })}
        </MenuItemWithIcon>
      </RestrictedByOrganisationPermissionWithDebug>
      {showUsedTemplateTypesItem && (
        <MenuItemWithIcon
          icon={<TemplateTypeIcon />}
          component={Link}
          to={`${buildDocPath(pathToDoc, doc.resolvedAsReadModelVersionNumber || null)}/template-types`}
        >
          {t("See template types", { ns: "Templates" })}
        </MenuItemWithIcon>
      )}
    </MenuButton>
  );
};
